/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  TLChatModelsResponse,
  TLHTTPValidationError,
  TLResolveModelIdentitiesRequest,
  TLResolveModelIdentitiesResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * List the models a user can select for the chat agent (catalog ∩ configured profiles).
 * @summary List Chat Models
 */
export const listChatModels = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLChatModelsResponse>({ url: `/llm/models`, method: 'GET', signal }, options);
};

export const getListChatModelsQueryKey = () => {
  return ['llm', 'models'] as const;
};

export const getListChatModelsQueryOptions = <
  TData = Awaited<ReturnType<typeof listChatModels>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListChatModelsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listChatModels>>> = ({ signal }) =>
    listChatModels(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listChatModels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListChatModelsQueryResult = NonNullable<Awaited<ReturnType<typeof listChatModels>>>;
export type ListChatModelsQueryError = ErrorType<unknown>;

export function useListChatModels<TData = Awaited<ReturnType<typeof listChatModels>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChatModels>>,
          TError,
          Awaited<ReturnType<typeof listChatModels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListChatModels<TData = Awaited<ReturnType<typeof listChatModels>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChatModels>>,
          TError,
          Awaited<ReturnType<typeof listChatModels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListChatModels<TData = Awaited<ReturnType<typeof listChatModels>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Chat Models
 */

export function useListChatModels<TData = Awaited<ReturnType<typeof listChatModels>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListChatModelsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Chat Models
 */
export const prefetchListChatModelsQuery = async <
  TData = Awaited<ReturnType<typeof listChatModels>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListChatModelsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Chat Models
 */
export const invalidateListChatModels = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListChatModelsQueryKey() }, options);

  return queryClient;
};

/**
 * Resolve stored ids (including DEPRECATED/RETIRED/repriced) to catalog identity + status.
 *
 * Backs the Loom node picker and execution view, which hold a bound id — a stable `model_id` or a
 * legacy `cost_id` — that may no longer be in the selectable `GET /llm/models` set. Each id resolves
 * catalog-first, then falls back to the cost_id path. Unknown or unresolvable ids are omitted. The
 * request field is `ids` (the legacy `cost_ids` body key is still accepted as a deprecated alias).
 * @summary Resolve Model Identities
 */
export const resolveModelIdentities = (
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLResolveModelIdentitiesResponse>(
    {
      url: `/llm/models/resolve`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLResolveModelIdentitiesRequest,
      signal,
    },
    options,
  );
};

export const getResolveModelIdentitiesQueryKey = (
  tLResolveModelIdentitiesRequest?: BodyType<TLResolveModelIdentitiesRequest>,
) => {
  return ['POST', 'llm', 'models', 'resolve', tLResolveModelIdentitiesRequest] as const;
};

export const getResolveModelIdentitiesQueryOptions = <
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getResolveModelIdentitiesQueryKey(tLResolveModelIdentitiesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof resolveModelIdentities>>> = ({ signal }) =>
    resolveModelIdentities(tLResolveModelIdentitiesRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof resolveModelIdentities>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ResolveModelIdentitiesQueryResult = NonNullable<Awaited<ReturnType<typeof resolveModelIdentities>>>;
export type ResolveModelIdentitiesQueryError = ErrorType<TLHTTPValidationError>;

export function useResolveModelIdentities<
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof resolveModelIdentities>>,
          TError,
          Awaited<ReturnType<typeof resolveModelIdentities>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveModelIdentities<
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof resolveModelIdentities>>,
          TError,
          Awaited<ReturnType<typeof resolveModelIdentities>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveModelIdentities<
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Resolve Model Identities
 */

export function useResolveModelIdentities<
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getResolveModelIdentitiesQueryOptions(tLResolveModelIdentitiesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Resolve Model Identities
 */
export const prefetchResolveModelIdentitiesQuery = async <
  TData = Awaited<ReturnType<typeof resolveModelIdentities>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveModelIdentities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getResolveModelIdentitiesQueryOptions(tLResolveModelIdentitiesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Resolve Model Identities
 */
export const invalidateResolveModelIdentities = async (
  queryClient: QueryClient,
  tLResolveModelIdentitiesRequest: BodyType<TLResolveModelIdentitiesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getResolveModelIdentitiesQueryKey(tLResolveModelIdentitiesRequest) },
    options,
  );

  return queryClient;
};
